/**
 * EditRouteDialog — extraído de Dashboard.js (refactor Fev/2026).
 *
 * Modal para admin editar uma rota existente: metadados, capa,
 * logos, fotos, visibilidade, grupos e substituir GPX/KML mantendo
 * reviews/ratings.
 *
 * Props:
 *   - open, onOpenChange       (estado do Dialog)
 *   - data, setData            (estado do formulário)
 *   - editSaving               (loading do submit)
 *   - onSubmit                 (handler async - form event)
 *   - availableGroups          (lista de grupos p/ visibilidade)
 *   - reloadFile, setReloadFile, reloadSaving, onReloadContent  (recarregar GPX/KML)
 *   - patchRoute(routeId, patch)  (callback que sincroniza routes/filteredRoutes/selectedRoute no pai)
 *   - api                      (URL base para chamadas de capa/logos/fotos)
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2 } from 'lucide-react';

const CATEGORIES = [
  { id: 'suv-4x4', label: '🚙 SUV 4x4', kind: 'type' },
  { id: '4x4-reductoras', label: '🚜 4x4 Redutoras', kind: 'type' },
  { id: 'car', label: '🚗 Carro', kind: 'badge' },
  { id: 'moto', label: '🏍️ Mota', kind: 'badge' },
];

export const EditRouteDialog = ({
  open,
  onOpenChange,
  data,
  setData,
  editSaving,
  onSubmit,
  availableGroups,
  reloadFile,
  setReloadFile,
  reloadSaving,
  onReloadContent,
  patchRoute,
  api,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar Rota</DialogTitle>
          <DialogDescription>Atualize os detalhes da rota.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome *</Label>
            <Input
              id="edit-name"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              required
              data-testid="edit-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">Descrição</Label>
            <Textarea
              id="edit-description"
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              data-testid="edit-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-difficulty">Dificuldade</Label>
              <Select
                value={data.difficulty}
                onValueChange={(value) => setData({ ...data, difficulty: value })}
              >
                <SelectTrigger id="edit-difficulty" data-testid="edit-difficulty-select">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Fácil</SelectItem>
                  <SelectItem value="moderate">Moderada</SelectItem>
                  <SelectItem value="hard">Difícil</SelectItem>
                  <SelectItem value="expert">Especialista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoria (marca as que se aplicam)</Label>
              <div className="grid grid-cols-2 gap-1 border border-slate-200 rounded-md p-2 bg-slate-50">
                {CATEGORIES.map((opt) => {
                  const checked = opt.kind === 'type'
                    ? data.route_type === opt.id
                    : (data.vehicle_badges || []).includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition ${
                        checked ? 'bg-white ring-1 ring-primary/40' : 'hover:bg-white/60'
                      }`}
                      data-testid={`edit-cat-opt-${opt.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          if (opt.kind === 'type') {
                            setData({
                              ...data,
                              route_type: v ? opt.id : (data.route_type === opt.id ? '' : data.route_type),
                            });
                          } else {
                            const cur = new Set(data.vehicle_badges || []);
                            if (v) cur.add(opt.id); else cur.delete(opt.id);
                            setData({ ...data, vehicle_badges: Array.from(cur) });
                          }
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500">SUV 4x4 e 4x4 Redutoras são exclusivos entre si.</p>
            </div>
          </div>

          {/* Foto de capa */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              🖼️ Foto de capa
              {data.has_cover === false && (
                <span className="text-xs text-slate-400">sem capa</span>
              )}
            </Label>
            {data.has_cover ? (
              <div className="relative group inline-block" data-testid="edit-cover-preview-wrapper">
                <img
                  src={`${api}/routes/${data.route_id}/cover?v=${data._coverV || 0}`}
                  alt="Capa"
                  className="w-full max-w-xs h-40 rounded-lg object-cover border border-slate-200"
                  data-testid="edit-cover-preview"
                />
                <button
                  type="button"
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-red-600/95 text-white flex items-center justify-center hover:bg-red-700 shadow"
                  onClick={async () => {
                    if (!window.confirm('Remover a foto de capa desta rota?')) return;
                    try {
                      await axios.delete(`${api}/routes/${data.route_id}/cover`, { withCredentials: true });
                      setData({ ...data, has_cover: false, _coverV: (data._coverV || 0) + 1 });
                      patchRoute(data.route_id, { has_cover: false, cover_image_path: null });
                      toast.success('Capa removida');
                    } catch (err) {
                      toast.error('Falha a remover capa');
                    }
                  }}
                  data-testid="edit-cover-remove-btn"
                  aria-label="Remover capa"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            <Input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append('file', f);
                try {
                  await axios.post(`${api}/routes/${data.route_id}/cover`, fd, { withCredentials: true });
                  setData({ ...data, has_cover: true, _coverV: (data._coverV || 0) + 1 });
                  patchRoute(data.route_id, { has_cover: true });
                  toast.success(data.has_cover ? 'Capa substituída' : 'Capa carregada');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Erro ao enviar capa');
                } finally {
                  e.target.value = '';
                }
              }}
              data-testid="edit-cover-input"
            />
            <p className="text-[10px] text-slate-500">
              JPG, PNG ou WEBP · máx 5 MB · usado como thumbnail nas listas
            </p>
          </div>

          {/* Logos existentes */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              🏷️ Logos (patrocinadores/clubes)
              <span className="text-xs text-slate-500">{data.logo_count || 0}/5</span>
            </Label>
            {(data.logo_count || 0) > 0 && (
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: data.logo_count }).map((_, i) => (
                  <div key={i} className="relative" data-testid={`edit-logo-existing-${i}`}>
                    <img
                      src={`${api}/routes/${data.route_id}/logos/${i}`}
                      alt=""
                      className="h-12 w-12 rounded-lg object-cover border border-slate-200"
                    />
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold leading-none flex items-center justify-center hover:bg-red-700"
                      onClick={async () => {
                        if (!window.confirm('Remover este logo?')) return;
                        try {
                          const r = await axios.delete(`${api}/routes/${data.route_id}/logos/${i}`, { withCredentials: true });
                          const newCount = r.data.logo_count;
                          setData({ ...data, logo_count: newCount });
                          patchRoute(data.route_id, { logo_count: newCount });
                          toast.success('Logo removido');
                        } catch (err) {
                          toast.error('Falha a remover logo');
                        }
                      }}
                      data-testid={`edit-logo-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {(data.logo_count || 0) < 5 && (
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, 5 - (data.logo_count || 0));
                  if (files.length === 0) return;
                  const fd = new FormData();
                  files.forEach((f) => fd.append('files', f));
                  try {
                    const r = await axios.post(`${api}/routes/${data.route_id}/logos`, fd, { withCredentials: true });
                    const newCount = r.data.logo_count;
                    setData({ ...data, logo_count: newCount });
                    patchRoute(data.route_id, { logo_count: newCount });
                    toast.success(`${r.data.added} ${r.data.added === 1 ? 'logo adicionado' : 'logos adicionados'}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Falha a enviar logos');
                  } finally {
                    e.target.value = '';
                  }
                }}
                data-testid="edit-logos-input"
              />
            )}
          </div>

          {/* Fotos existentes */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              📸 Galeria de fotos
              <span className="text-xs text-slate-500">{data.photo_count || 0}/10</span>
            </Label>
            {(data.photo_count || 0) > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {Array.from({ length: data.photo_count }).map((_, i) => (
                  <div key={i} className="relative aspect-video rounded-lg overflow-hidden bg-muted group" data-testid={`edit-photo-existing-${i}`}>
                    <img
                      src={`${api}/routes/${data.route_id}/photos/${i}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={async () => {
                        if (!window.confirm('Remover esta foto?')) return;
                        try {
                          const r = await axios.delete(`${api}/routes/${data.route_id}/photos/${i}`, { withCredentials: true });
                          const newCount = r.data.photo_count;
                          setData({ ...data, photo_count: newCount });
                          patchRoute(data.route_id, { photo_count: newCount });
                          toast.success('Foto removida');
                        } catch (err) {
                          toast.error('Falha a remover foto');
                        }
                      }}
                      data-testid={`edit-photo-remove-${i}`}
                      aria-label="Remover"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {(data.photo_count || 0) < 10 && (
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, 10 - (data.photo_count || 0));
                  if (files.length === 0) return;
                  const fd = new FormData();
                  files.forEach((f) => fd.append('files', f));
                  try {
                    const r = await axios.post(`${api}/routes/${data.route_id}/photos`, fd, { withCredentials: true });
                    const newCount = r.data.photo_count;
                    setData({ ...data, photo_count: newCount });
                    patchRoute(data.route_id, { photo_count: newCount });
                    toast.success(`${r.data.added} ${r.data.added === 1 ? 'foto adicionada' : 'fotos adicionadas'}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Falha a enviar fotos');
                  } finally {
                    e.target.value = '';
                  }
                }}
                data-testid="edit-photos-input"
              />
            )}
            <p className="text-[10px] text-slate-500">
              JPG, PNG ou WEBP · máx 8 MB por foto · até 10 fotos · guardadas imediatamente ao seleccionar
            </p>
          </div>

          {/* Visibility */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              🔒 Visibilidade da rota
              {data.is_private ? (
                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">PRIVADA</span>
              ) : (
                <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">PÚBLICA</span>
              )}
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setData({ ...data, is_private: false })}
                className={`flex-1 px-3 py-2 rounded-md text-xs font-bold transition border-2 ${
                  !data.is_private
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
                data-testid="route-visibility-public-btn"
              >
                🌍 PÚBLICA<br /><span className="text-[10px] font-normal opacity-80">Qualquer utilizador vê</span>
              </button>
              <button
                type="button"
                onClick={() => setData({ ...data, is_private: true })}
                className={`flex-1 px-3 py-2 rounded-md text-xs font-bold transition border-2 ${
                  data.is_private
                    ? 'bg-amber-600 text-white border-amber-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-amber-400'
                }`}
                data-testid="route-visibility-private-btn"
              >
                🔒 PRIVADA<br /><span className="text-[10px] font-normal opacity-80">Só membros dos grupos abaixo</span>
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Mesmo PÚBLICA, podes associar grupos abaixo (apenas para tag/organização). Em PRIVADA, só membros dos grupos selecionados conseguem ver e navegar a rota.
            </p>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
              {availableGroups.length === 0 && (
                <span className="text-xs text-slate-400">Sem grupos criados ainda.</span>
              )}
              {availableGroups.map((g) => {
                const selected = (data.group_ids || []).includes(g.group_id);
                return (
                  <button
                    key={g.group_id}
                    type="button"
                    onClick={() => {
                      const cur = new Set(data.group_ids || []);
                      if (cur.has(g.group_id)) cur.delete(g.group_id); else cur.add(g.group_id);
                      setData({ ...data, group_ids: Array.from(cur) });
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                      selected
                        ? 'bg-slate-900 text-white border-2 border-slate-900'
                        : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`route-group-pick-${g.group_id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: g.color }} />
                    {g.name}
                    <span className="text-[10px] opacity-70">({g.member_count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recarregar GPX/KML */}
          <div className="space-y-2 border-t pt-3">
            <Label className="flex items-center gap-2">
              🔁 Recarregar nova versão do GPX/KML
            </Label>
            <p className="text-xs text-slate-500">
              Substitui o ficheiro desta rota mantendo o <strong>route_id</strong>, reviews, comentários, ratings e downloads históricos. As estatísticas (km, elevação, duração, perfil, POIs) são recalculadas.
            </p>
            <Input
              type="file"
              accept=".gpx,.kml"
              onChange={(e) => setReloadFile(e.target.files?.[0] || null)}
              data-testid="edit-reload-file-input"
            />
            {reloadFile && (
              <p className="text-xs text-slate-600">
                Ficheiro escolhido: <strong>{reloadFile.name}</strong> ({Math.round(reloadFile.size / 1024)} KB)
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full border-amber-500 text-amber-700 hover:bg-amber-50"
              disabled={!reloadFile || reloadSaving}
              onClick={onReloadContent}
              data-testid="edit-reload-content-button"
            >
              {reloadSaving ? 'A recarregar...' : '🔁 Substituir GPX/KML desta rota'}
            </Button>
          </div>
          <Button type="submit" className="w-full" disabled={editSaving} data-testid="edit-submit-button">
            {editSaving ? 'A guardar...' : 'Guardar alterações'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRouteDialog;
